import ThumbnailImage from './ThumbnailImage';
import BookingServiceThumbnailImage from './BookingServiceThumbnailImage';
import ZoomSetting, { ZoomSettingInit } from './ZoomSetting';

export const Schemes = {
  EVENT: 0,
  REQUEST: 1,
  AVAILABILITY: 2,
  ASSIGN: 6,
  SCHOOL: 7,
} as const;

export interface CoverContentInit {
  type: string;
  embed: string;
  image: ThumbnailImage;
}

export class CoverContent {
  public readonly type: string;
  public readonly embed: string;
  public readonly image: ThumbnailImage;

  constructor(init: CoverContentInit) {
    this.type = init.type;
    this.embed = init.embed;
    this.image = init.image;
  }
}

export interface BookingServiceSettingInit {
  isPrepayable: boolean;
  isPayableOnSite: boolean;
  isPayableWithSubscription: boolean;
  isPayableWithTicket: boolean;
  isStaffSelectable: boolean;
}

export class BookingServiceSetting {
  public readonly isPrepayable: boolean;
  public readonly isPayableOnSite: boolean;
  public readonly isPayableWithSubscription: boolean;
  public readonly isPayableWithTicket: boolean;
  public readonly isStaffSelectable: boolean;

  constructor(init: BookingServiceSettingInit) {
    this.isPrepayable = init.isPrepayable;
    this.isPayableOnSite = init.isPayableOnSite;
    this.isPayableWithSubscription = init.isPayableWithSubscription;
    this.isPayableWithTicket = init.isPayableWithTicket;
    this.isStaffSelectable = init.isStaffSelectable;
  }
}

export interface BookingServiceInit {
  isPasswordProtected: boolean;
  isPublishedWithPasswordProtected: boolean;
  jsonDescription: string;
  name: string;
  publicId: number;
  setting: BookingServiceSetting;
  scheme: number;
  initialSettingStatus: number;
  coverContentsType: number;
  description: string;
  publishedAt: string;
  lowestPrice: number;
  tagNames: string[];
  coverContent: CoverContent;
  primaryImage: BookingServiceThumbnailImage;
  secondaryImages?: BookingServiceThumbnailImage[];
  isFullyBooked: boolean;
  zoomSetting: ZoomSettingInit;
}

export default class BookingService {
  public readonly isPasswordProtected: boolean;
  public readonly isPublishedWithPasswordProtected: boolean;
  public readonly jsonDescription: string;
  public readonly name: string;
  public readonly publicId: number;
  public readonly setting: BookingServiceSetting;
  public readonly scheme: number;
  public readonly initialSettingStatus: number;
  public readonly coverContentsType: number;
  public readonly description: string;
  public readonly publishedAt: string;
  public readonly lowestPrice: number;
  public readonly tagNames: string[];
  public readonly coverContent?: CoverContent;
  public readonly thumbnailUrl: string;
  public readonly primaryImage: BookingServiceThumbnailImage;
  public readonly secondaryImages?: BookingServiceThumbnailImage[];
  public readonly isFullyBooked: boolean;
  public readonly zoomSetting: ZoomSettingInit;

  constructor(init: BookingServiceInit) {
    this.isPasswordProtected = init.isPasswordProtected;
    this.isPublishedWithPasswordProtected =
      init.isPublishedWithPasswordProtected;
    this.jsonDescription = init.jsonDescription;
    this.name = init.name;
    this.publicId = init.publicId;
    this.setting = init.setting;
    this.scheme = init.scheme;
    this.initialSettingStatus = init.initialSettingStatus;
    this.coverContentsType = init.coverContentsType;
    this.description = init.description;
    this.publishedAt = init.publishedAt;
    this.lowestPrice = init.lowestPrice;
    this.tagNames = init.tagNames;
    this.coverContent = init.coverContent;
    this.thumbnailUrl = this.initThumbnailUrl();
    this.primaryImage = init.primaryImage;
    this.secondaryImages = init.secondaryImages;
    this.isFullyBooked = init.isFullyBooked;
    this.zoomSetting = new ZoomSetting(init.zoomSetting);
  }

  private initThumbnailUrl = (): string => {
    return this.coverContent ? this.coverContent.image.square.url : '';
  };
}
